/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useEffect, useState } from "react";
import {
  Button,
  Form,
  Input,
  Select,
  Table,
  Tag,
  Tooltip,
  Typography,
} from "antd";
import { ColumnsType } from "antd/es/table";
import {
  CrossIcon,
  DeleteIcon,
  PencilIcon,
  PlusIcon,
  SelectArrow,
  TickIcon,
} from "../../../../../atoms/CustomIcons";
import { useLocation } from "react-router-dom";
import {
  createTakeoffByCompanyId,
  deleteTakeoff,
  deleteTakeoffById,
} from "../../ducks/services";
import { AxiosError } from "axios";
import SuccessMessage from "../../../../../atoms/Toasts/SuccessMessage";
import ErrorMessage from "../../../../../atoms/Toasts/ErrorMessage";
import { takeOffTableType } from "../../ducks/types";
import { getTakeoffById, getUnitsFormData } from "../../ducks/actions";
import { useAppDispatch, useAppSelector } from "../../../../../config/hooks";
import ConfirmDelete from "../../../../../atoms/ConfirmDelete";
import { PopUp } from "../../../../../atoms/PopUp";
import { createStyles } from "antd-style";
import { TableRowSelection } from "antd/es/table/interface";
import { getCompanyFieldsById } from "../../../TagsCategories/ducks/actions";
import { getUnitsById } from "../../../MeasurementsUnits/ducks/actions";

interface DataType {
  id?: string;
  name: string | null;
  unit_id: string | null;
  category_ids: string[] | null;
}

interface EditableCellProps {
  editing: boolean;
  dataIndex: string;
  title: string;
  inputType: "number" | "text";
  record: DataType;
  index: number;
}

interface CategoryType {
  label: string;
  value: string;
}

const EditableCell: React.FC<React.PropsWithChildren<EditableCellProps>> = ({
  editing,
  dataIndex,
  title,
  inputType,
  record,
  index,
  children,
  ...restProps
}) => {
  const [categoryDropDown, setCategoryDropDown] = useState<CategoryType[]>([]);
  const tagItems = useAppSelector((state) => state?.tags?.tagData);
  const unitDropdown = useAppSelector((state) => state?.takeOff?.unitFormData);

  const [inputValue, setInputValue] = useState("");

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let processedValue = e.target.value;

    if (inputValue === "" && processedValue.startsWith(" ")) {
      processedValue = processedValue.trimStart();
    }

    setInputValue(processedValue);
  };

  useEffect(() => {
    if (tagItems?.data && tagItems?.data?.length > 0) {
      const dummyArray: CategoryType[] = [];
      tagItems?.data?.map((item: { name: string; id: string; color: string }) =>
        dummyArray.push({
          label: item?.name,
          value: item?.id,
        })
      );
      setCategoryDropDown(dummyArray);
    } else {
      setCategoryDropDown([]);
    }
  }, [tagItems]);

  const inputNode =
    dataIndex === "unit_id" || dataIndex === "category_ids" ? (
      <Select
        options={dataIndex === "category_ids" ? categoryDropDown : unitDropdown}
        placeholder={
          dataIndex === "unit_id" ? "Unit of measurement" : "Categories"
        }
        mode={dataIndex === "category_ids" ? "multiple" : undefined}
        maxTagCount={1}
        suffixIcon={<SelectArrow />}
        menuItemSelectedIcon={<TickIcon style={{ color: "#00A07D" }} />}
        className="editTableSelect"
      />
    ) : (
      <Input
        placeholder="Enter name"
        className="editTableInput"
        value={inputValue} // Make sure the input is a controlled component
        onChange={handleChange}
      />
    );

  return (
    <td {...restProps}>
      {editing ? (
        <Form.Item
          name={dataIndex}
          style={{ margin: 0 }}
          rules={[
            ...(dataIndex !== "category_ids"
              ? [
                  {
                    required: true,
                    message: "",
                  },
                ]
              : []),
            {
              validator: (_, value) => {
                if (!value) {
                  return Promise.resolve();
                }
                if (typeof value === "string" && value.startsWith(" ")) {
                  return Promise.reject(new Error(""));
                }
                return Promise.resolve();
              },
            },
          ]}
        >
          {inputNode}
        </Form.Item>
      ) : (
        children
      )}
    </td>
  );
};

interface PropsType {
  data: takeOffTableType[] | null;
}

export default function EditableTable(props: PropsType) {
  const useStyle = createStyles(({ css }) => {
    return {
      customTable: css`
        .ant-table {
          .ant-table-container {
            .ant-table-body,
            .ant-table-content {
              scrollbar-width: thin;
              scrollbar-color: transparent transparent;
              scrollbar-gutter: stable;
              scrollbar-border-radius: 30px;
              transition: scrollbar-color 0.5s;

              &:hover {
                scrollbar-color: #dee5e6 transparent;
              }
            }
            .ant-table-thead {
              .ant-table-cell {
                height: 40px;
              }
            }
          }
        }
      `,
    };
  });
  const { styles } = useStyle();
  const { data } = props;
  const [form] = Form.useForm();
  const dispatch = useAppDispatch();
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isDeleteModalMultiple, setIsDeleteModalMultiple] = useState(false);
  const [loading, setLoading] = useState(false);
  const [dataSource, setDataSource] = useState<DataType[]>([]);
  const [editingKey, setEditingKey] = useState<null | string>(null);
  const [deleteId, setDeleteId] = useState<null | string>(null);
  const location = useLocation();
  const pathSegments = location.pathname.split("/");
  const companyId = pathSegments[pathSegments.length - 1];
  const unitListing = useAppSelector((state) => state?.unit?.unitsData);
  const [selectedRowKeys, setSelectedRowKeys] = useState<
    React.Key[] | string[]
  >([]);

  const tagItems = useAppSelector((state) => state?.tags?.tagData);

  useEffect(() => {
    if (companyId) {
      const payload = {
        company_id: companyId,
        module: "MATERIALS",
      };

      dispatch(getCompanyFieldsById(payload));
    }
  }, [dispatch, companyId]);

  const onSelectChange = (newSelectedRowKeys: React.Key[]) => {
    setSelectedRowKeys(newSelectedRowKeys);
  };

  const rowSelection: TableRowSelection<DataType> = {
    selectedRowKeys,
    onChange: onSelectChange,
    type: "checkbox",
    getCheckboxProps: (record: DataType) => ({
      disabled: record.id === "1",
      name: record.name ?? undefined,
    }),
  };

  useEffect(() => {
    if (data && data?.length > 0) {
      setDataSource(data);
    } else {
      setDataSource([]);
    }
  }, [data]);

  const isEditing = (record: DataType) => record.id === editingKey;

  const edit = (record: Partial<DataType>) => {
    form.setFieldsValue({ name: "", unit_id: "", category_ids: "", ...record });
    setEditingKey(record?.id ?? null);
  };

  const cancel = () => {
    if (editingKey !== null) {
      // Check if the row being cancelled is the newly added row (has null values)
      const isNewRow = dataSource?.some((item) => item.id === "1");

      if (isNewRow) {
        // Remove the newly added row
        setDataSource(dataSource?.filter((item) => item.id !== editingKey));
        setEditingKey(null);
      } else {
        // If it's an existing row being edited, just cancel editing
        setEditingKey(null);
      }
    }
  };

  const handleAdd = () => {
    const newData: DataType = {
      id: "1",
      name: null,
      unit_id: null,
      category_ids: null,
    };
    setDataSource([newData, ...(dataSource || [])]);
    edit(newData);
  };

  const save = async () => {
    const item = (await form.validateFields()) as DataType;
    const newData: DataType[] = [...(dataSource || [])];
    const index = newData.findIndex((item) => editingKey === item.id);
    const payload = {
      id: editingKey === "1" ? undefined : editingKey,
      name: item?.name,
      unit_id: item?.unit_id,
      category_ids:
        item?.category_ids && item?.category_ids?.length > 0
          ? item?.category_ids
          : [],
    };

    createTakeoffByCompanyId(companyId, payload)
      .then(() => {
        SuccessMessage(
          `Takeoff measurements ${editingKey === "1" ? "added" : "updated"}`
        );
        if (item.id !== "1") {
          const oldItems = newData[index];
          newData.splice(index, 1, {
            ...oldItems,
            ...item,
          });
          setDataSource(newData);
          setEditingKey(null);
        } else {
          newData.push(item);
          setDataSource(newData);
          setEditingKey(null);
        }
        dispatch(getTakeoffById(companyId));
      })
      .catch((e: unknown) => {
        if (e instanceof AxiosError) {
          ErrorMessage(
            e?.response?.data?.error?.message ?? "Something went wrong"
          );
        } else {
          ErrorMessage("Something went wrong");
        }
      });
  };

  useEffect(() => {
    if (companyId) {
      dispatch(getUnitsById(companyId));
      dispatch(getUnitsFormData(companyId));
    }
  }, [companyId, dispatch]);

  const apiSubmitDeleteTakeOff = () => {
    setLoading(true);
    deleteTakeoffById(deleteId ?? "")
      .then(() => {
        SuccessMessage("Takeoff measurements deleted");
        setIsDeleteModalOpen(false);
        setSelectedRowKeys([]);
        setDeleteId(null);
        setEditingKey(null);
        dispatch(getTakeoffById(companyId));
      })
      .catch((e: unknown) => {
        if (e instanceof AxiosError) {
          ErrorMessage(
            e?.response?.data?.error?.message ?? "Something went wrong"
          );
        } else {
          ErrorMessage("Something went wrong");
        }
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const apiSubmitDeleteMultiple = () => {
    setLoading(true);
    deleteTakeoff(selectedRowKeys)
      .then(() => {
        SuccessMessage("Takeoff measurements deleted");
        setIsDeleteModalMultiple(false);
        setSelectedRowKeys([]);
        dispatch(getTakeoffById(companyId));
      })
      .catch((e: unknown) => {
        if (e instanceof AxiosError) {
          ErrorMessage(
            e?.response?.data?.error?.message ?? "Something went wrong"
          );
        } else {
          ErrorMessage("Something went wrong");
        }
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const ConfirmDeletePopUp = {
    visibility: isDeleteModalOpen,
    content: (
      <ConfirmDelete
        title=""
        onClose={() => setIsDeleteModalOpen(false)}
        heading="Confirm deletion"
        paragraph="This item will not be available anymore. This action cannot be undone."
        btnText="Delete"
        btnApi={() => apiSubmitDeleteTakeOff()}
        loading={loading}
      />
    ),
    width: 480,
    onCancel: () => setIsDeleteModalOpen(false),
  };

  const ConfirmDeletePopUpMultiple = {
    visibility: isDeleteModalMultiple,
    content: (
      <ConfirmDelete
        title=""
        onClose={() => setIsDeleteModalMultiple(false)}
        heading="Confirm deletion"
        paragraph={`Selected ${
          selectedRowKeys?.length === 1 ? "item" : "items"
        } will not be available anymore. This action cannot be undone.`}
        btnText={`Delete ${selectedRowKeys?.length} ${
          selectedRowKeys?.length === 1 ? "item" : "items"
        }`}
        btnApi={() => apiSubmitDeleteMultiple()}
        loading={loading}
      />
    ),
    width: 480,
    onCancel: () => setIsDeleteModalMultiple(false),
  };

  const columns = [
    {
      title: (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          Takeoff measurement name
          <Button
            className="btn-sm"
            onClick={handleAdd}
            icon={<PlusIcon />}
            disabled={dataSource?.some((item) => item.id === "1")}
          />
        </div>
      ),
      dataIndex: "name",
      width: 380,
      editable: true,
    },
    {
      title: "Unit of measurement",
      dataIndex: "unit_id",
      width: 230,
      editable: true,
      render: (text: string) =>
        text && unitListing
          ? unitListing?.data?.find((item: { id: string }) => item?.id === text)
              ?.name
          : "-",
    },
    {
      title: "Categories",
      dataIndex: "category_ids",
      width: 286,
      editable: true,
      render: (text: string[]) =>
        text?.length > 0 ? (
          <div style={{ display: "flex", flexWrap: "wrap", gap: "4px" }}>
            {text?.slice(0, 1).map((item: string) => {
              const tagDetails: { color: string; name: string } =
                tagItems?.data?.find(
                  (tag: { id: string }) => tag?.id === item
                ) || { color: "", name: "" };

              return (
                <Tag
                  key={item}
                  style={{ textTransform: "capitalize" }}
                  // className={tagDetails?.color}
                >
                  {tagDetails?.name}
                </Tag>
              );
            })}

            {text?.length > 1 && (
              <Tooltip
                title={text
                  ?.slice(1)
                  ?.map((itemId: string) => {
                    const tagDetails: { name: string } = tagItems?.data?.find(
                      (tag: { id: string }) => tag?.id === itemId
                    ) || { name: "" };
                    return tagDetails?.name;
                  })
                  ?.join(", ")}
              >
                <Tag
                  style={{
                    background: "#EFF2F3",
                    color: "#151718",
                    border: "none",
                    cursor: "pointer",
                  }}
                >
                  +{text?.length - 1}
                </Tag>
              </Tooltip>
            )}
          </div>
        ) : (
          "-"
        ),
    },
    {
      title: "",
      width: 104,
      dataIndex: "status",
      render: (_: string, record: DataType) => {
        const editable = isEditing(record);

        return editable ? (
          <span className="actionIcons">
            <Typography.Link
              onClick={() => save()}
              style={{ marginInlineEnd: 8 }}
            >
              <TickIcon style={{ color: "rgb(21, 23, 24)" }} />
            </Typography.Link>
            <Typography.Link onClick={cancel} style={{ marginInlineEnd: 8 }}>
              <CrossIcon style={{ color: "rgb(21, 23, 24)" }} />
            </Typography.Link>
          </span>
        ) : (
          <span className="actionIcons">
            <Typography.Link
              disabled={editingKey !== null}
              onClick={() => edit(record)}
              style={{ marginInlineEnd: 8 }}
            >
              <PencilIcon />
            </Typography.Link>
            <Typography.Link
              disabled={editingKey !== null}
              onClick={() => {
                setDeleteId(record?.id ?? null);
                setIsDeleteModalOpen(true);
              }}
              className="deleteIconHover"
            >
              <DeleteIcon style={{ color: "rgb(21, 23, 24)" }} />
            </Typography.Link>
          </span>
        );
      },
    },
  ];

  console.log("deleteId", deleteId);

  const mergedColumns = columns.map((col) => {
    if (!col.editable) {
      return col;
    }
    return {
      ...col,
      onCell: (record: DataType) => ({
        record,
        inputType: "text",
        dataIndex: col.dataIndex,
        title: col.title,
        editing: isEditing(record),
      }),
    };
  });

  return (
    <Form form={form} component={false}>
      <Table<DataType>
        components={{
          body: { cell: EditableCell },
        }}
        className={styles.customTable}
        rowSelection={rowSelection}
        dataSource={dataSource}
        columns={mergedColumns as ColumnsType<DataType>}
        rowClassName="editable-row"
        pagination={false}
        rowKey="id"
      />

      {selectedRowKeys?.length !== 0 ? (
        <div className="selection_bar">
          <div className="selected_count">
            <Button
              icon={<CrossIcon style={{ color: "white" }} />}
              className="only_icon"
              type="text"
              onClick={() => setSelectedRowKeys([])}
            />
            {selectedRowKeys.length}{" "}
            {`${
              selectedRowKeys.length === 1
                ? "takeoff measurement"
                : "takeoff measurements"
            }`}{" "}
            selected
          </div>
          <div className="selection_buttons">
            <Button
              type="text"
              icon={<DeleteIcon style={{ color: "white" }} />}
              onClick={() => {
                setIsDeleteModalMultiple(true);
              }}
            >
              Delete
            </Button>
          </div>
        </div>
      ) : null}
      <PopUp {...ConfirmDeletePopUp} />
      <PopUp {...ConfirmDeletePopUpMultiple} />
    </Form>
  );
}
